import { action, makeObservable, observable } from "mobx";
import ActionsApi, { ActionsApi as IActionsApi } from "../../../../../../../../services/api/v2/actions/Actions.api";
import { BaseModel } from "../../../../../../../../core/util/BaseModel";
import { SingleFormModel } from "../../../../../../../change/forms/singleFormModel/SingleForm_model";
import I18n from "../../../../../../../../core/localization/I18n";
import ModalContext from "../../../../../../../../core/modalZ/context/ModalContext";
import { UiActionRenderers } from "@flightpath/coreui/dist/uiAction/IUiAction";
import ProjectTeamUserPermissionsApi, {
  ProjectTeamUserPermissionsApi as IProjectTeamUserPermissionsApi
} from "../../../../../../../../services/api/v2/projectTeamUserPermissions/ProjectTeamUserPermissions.api";
import { ButtonTypes } from "@flightpath/coreui/dist/ui/Button";
import ProgressIndicatorModel, {
  ProgressIndicatorModel as IProgressIndicatorModel
} from "../../../../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import ToasterService, { ToasterService as IToasterService } from "../../../../../../../../core/toaster/ToasterService";
import { TOASTER_TOAST_TIME } from "../../../../../../../../core/toaster/Toaster_model";
import { getBulkUpdateActionsFormFields } from "./ActionBulkUpdateSidebar_formFields";

export class ActionBulkUpdateSidebarModel extends BaseModel {
  actionsProvider: IActionsApi;
  @observable isLoading: boolean = true;
  @observable.ref projectTeamMembers: any[];
  projectTeamUserPermissionsProvider: IProjectTeamUserPermissionsApi;
  httpProgress: IProgressIndicatorModel;
  toasterService: IToasterService;
  projectId: number;
  organisationId: number;
  selectedActions: number[];
  closeModalFn: () => void;
  formModel: SingleFormModel;

  constructor(projectId: number, organisationId: number, selectedActions: number[], closeModalFn: () => void) {
    super();
    makeObservable(this);

    this.organisationId = organisationId;
    this.projectId = projectId;
    this.selectedActions = selectedActions;
    this.actionsProvider = ActionsApi;
    this.httpProgress = ProgressIndicatorModel;
    this.isLoading = true;
    this.toasterService = ToasterService;
    this.projectTeamUserPermissionsProvider = ProjectTeamUserPermissionsApi;
    this.closeModalFn = closeModalFn;
    this.initForm();
  }

  initForm = async () => {
    await this.loadProjectTeamMembers();
    this.formModel = new SingleFormModel();
    this.formModel.setActions([
      {
        id: "cancel",
        label: I18n.t("phrases.cancel"),
        onAction: ev => {
          ModalContext.hide();
        },
        componentProps: {
          className: "ml-auto"
        },
        rendersIn: UiActionRenderers.BUTTON
      },
      {
        id: "Save",
        label: I18n.t("phrases.save"),
        onAction: async () => {
          let formResult = await this.formModel.submit();

          let filteredFormRes = {};
          Object.entries(formResult).forEach(([key, val]) => {
            if (!!val && val !== "") {
              filteredFormRes[key] = val;
            } else {
              filteredFormRes[key] = null;
            }
          });

          if (formResult) {
            this.httpProgress.showOverlay();
            let res = await this.actionsProvider.bulkUpdateActions(
              this.organisationId,
              this.projectId,
              this.selectedActions,
              filteredFormRes as any
            );
            this.httpProgress.hideOverlay();

            if (!res || res.isError) {
              this.toasterService
                .showErrorToast(TOASTER_TOAST_TIME.SLOW)
                .setContent(<p>{I18n.t("errors.updateActions")}</p>);
              return;
            }

            this.closeModalFn();
            this.toasterService
              .showSuccessToast(TOASTER_TOAST_TIME.NORMAL)
              .setContent(<p>{`${I18n.t("phrases.actionsUpdatedSuccessfully")}`}</p>);
          }
        },
        rendersIn: UiActionRenderers.BUTTON,
        componentProps: {
          type: ButtonTypes.PRIMARY,
          className: "ml-2"
        }
      }
    ]);

    this.setFormModel();
    this.setIsLoading(false);
  };

  @action
  setProjectTeamMembers = projTeamMembers => {
    this.projectTeamMembers = projTeamMembers;
  };

  @action
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  @action
  setFormModel = () => {
    this.formModel.formFields = getBulkUpdateActionsFormFields(this.organisationId, this.projectTeamMembers);
  };

  @action
  loadProjectTeamMembers = async () => {
    const res = await this.projectTeamUserPermissionsProvider.getAllUsersSimple(this.organisationId, this.projectId);
    if (!res || res.isError) return;
    this.setProjectTeamMembers(res.payload);
  };
}
